import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  CloseButton,
} from "@headlessui/react";
import Autocomplete from "react-google-autocomplete";
import { ConfirmButton } from "@/components/button/confirmButton";
import MapPinIcon from "@/components/_icons/mapPinIcon";
import CloseIcon from "@/components/_icons/closeIcon";
import ErrorIcon from "@/mocks/assets/errorIcon.svg";
import Image from "next/image";

const PreferenceDropdown = ({ preference, setPreference }: any) => {
  return (
    <Popover className="relative">
      <PopoverButton
        className={[
          "flex",
          "items-center",
          "gap-x-2",
          "w-full",
          "leading-6",
          "text-gray-900",
          "hover:bg-gray-50",
        ].join(" ")}
      >
        <input
          type="text"
          id="delivery_preference_input"
          className="block px-2.5 h-[58px] py-2.5 w-full text-gray-900 bg-transparent rounded-[4px] border hover:border-black focus:outline-none focus:ring-1 focus:ring-blue border-gray-300 peer"
          value={
            preference === "contactless"
              ? "Leave it in front of my door"
              : "Meet me at my door"
          }
          onChange={(e) => setPreference(e.target.value)}
        />
        <label
          htmlFor="delivery_preference_input"
          className="absolute text-md text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-5 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-5 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
        >
          Delivery preference
        </label>
      </PopoverButton>
      <PopoverPanel
        transition
        className="absolute top-full z-20 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
      >
        <div className="p-3">
          <CloseButton className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 leading-6 hover:bg-gray-50">
            <button
              onClick={() => {
                setPreference("contactless");
              }}
            >
              <div className="flex-auto">
                <p>Leave it in front of my door</p>
              </div>
            </button>
          </CloseButton>
          <CloseButton className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 leading-6 hover:bg-gray-50">
            <button
              onClick={() => {
                setPreference("meetDoor");
              }}
            >
              <div className="flex-auto">
                <p>Meet me at my door</p>
              </div>
            </button>
          </CloseButton>
        </div>
      </PopoverPanel>
    </Popover>
  );
};

export const EditAddressesModal = ({
  isOpen,
  onRequestClose,
  onUpdated,
  merchantColor,
  address,
  setDefaultDeliveryAllowed = false,
  checkoutCloseHandler,
}: any) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const [addressOne, setAddressOne] = useState(address?.address1);
  const [addressTwo, setAddressTwo] = useState(address?.address2);
  const [city, setCity] = useState(address?.city);
  const [state, setState] = useState(address?.city);
  const [zip, setZip] = useState(address?.zip);
  const [tempAddress, setTempAddress] = useState("");

  const [preference, setPreference] = useState(address?.preference);
  const [note, setNote] = useState(address?.note);
  const [showAddressEmptyError, setShowAddressEmptyError] = useState(false);
  const [isDefaultDelivery, setIsDefaultDelivery] = useState(false);

  useEffect(() => {
    if (address) {
      setAddressOne(address?.address1);
      setAddressTwo(address?.address2);
      setCity(address?.city);
      setState(address?.state);
      setZip(address?.zip);
      setPreference(address?.preference);
      setNote(address?.note);
    }
  }, [address]);

  const handlePlaceSelected = (place: any) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(place.adr_address, "text/html");
    const regionSpan = doc.querySelector("span.region");
    const zipSpan = doc.querySelector("span.postal-code");
    const stateTrimmed = regionSpan!.textContent;
    const zipTrimmed = zipSpan!.textContent;
    setTempAddress(place.formatted_address);
    setAddressOne(place.name);
    setCity(place.vicinity);
    setState(stateTrimmed);
    setZip(zipTrimmed);
  };

  const handleTextChange = (event: any) => {
    setTempAddress(event.target.value);
    setShowAddressEmptyError(false);
  };

  const handleSubmit = async () => {
    if (addressOne === undefined || addressOne === "") {
      setShowAddressEmptyError(true);
    } else {
      setShowAddressEmptyError(false);
      const address = {
        address1: addressOne,
        address2: addressTwo,
        city: city,
        state: state,
        country: "US",
        zip: zip,
        preference,
        note,
      };
      onUpdated && onUpdated(address, isDefaultDelivery);
      onRequestClose && onRequestClose();
    }
  };

  const closeModal = () => {
    setShowAddressEmptyError(false);
    const scheduledTimeString =
      global?.window?.localStorage.getItem("scheduledTime");
    const sessionTime = scheduledTimeString
      ? JSON.parse(scheduledTimeString)
      : {};

    global?.window?.localStorage.setItem(
      "scheduledTime",
      JSON.stringify({
        orderType: "pickup",
        date: sessionTime?.date || null,
        time: sessionTime?.time || null,
      })
    );
    checkoutCloseHandler && checkoutCloseHandler();
    onRequestClose && onRequestClose();
  };

  return (
    <ReactModal
      ariaHideApp={false}
      appElement={global?.window?.document?.getElementById("app") || undefined}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="fixed inset-0 flex items-center justify-center"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-50"
    >
      <div className="bg-white sm:rounded-lg shadow-lg w-[100%] h-[100%] sm:h-[auto] sm:w-[70%] mx-auto overflow-hidden">
        <div className="flex justify-between items-center px-6 pt-6">
          <button
            onClick={closeModal}
            className="text-gray-600 hover:text-gray-900 ml-auto"
          >
            <CloseIcon />
          </button>
        </div>
        <div className="px-4 pb-4 flex flex-col justify-center items-center">
          <MapPinIcon brandingColor={merchantColor} />
          <p className="mt-4 text-3xl font-medium text-center text-[#373737] text-[32px]">
            Edit your delivery info
          </p>
          <p className="mt-4 text-base leading-6 text-center text-[15px] text-neutral-700 w-full">
            Please make sure your address and delivery preferences are correct.
          </p>
          <div className="pt-10 w-full sm:w-[80%] grid sm:grid-cols-2 gap-y-8 sm:gap-x-6">
            <div className="relative">
              <div>
                <Autocomplete
                  apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || ""}
                  onChange={handleTextChange}
                  onPlaceSelected={handlePlaceSelected}
                  id="delivery_address1_input"
                  options={{
                    types: ["address"],
                    fields: ["all"],
                  }}
                  value={tempAddress}
                  className={`block px-2.5 h-[58px] py-2.5 w-full text-gray-900 bg-transparent rounded-[4px] border hover:border-black focus:outline-none focus:ring-1 ${
                    showAddressEmptyError
                      ? "border-red-600 focus:ring-red-600"
                      : "border-gray-300 focus:ring-blue"
                  } peer`}
                />
                <label
                  htmlFor="delivery_address1_input"
                  className={`absolute text-md duration-300 transform -translate-y-5 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 ${
                    showAddressEmptyError
                      ? "text-red-600 peer-focus:text-red-600"
                      : "text-gray-500 peer-focus:text-blue-600"
                  }`}
                >
                  Delivery address
                </label>
              </div>
              <div
                className={`absolute flex flex-row gap-2 top-full mt-1 text-sm text-red-600 transition-opacity ${
                  showAddressEmptyError ? "opacity-100" : "opacity-0"
                }`}
              >
                <Image className="" src={ErrorIcon} alt="error icon" />
                Please select a valid address.
              </div>
            </div>
            <div className="relative">
              <input
                type="text"
                id="delivery_address2_input"
                className="block px-2.5 h-[58px] py-2.5 w-full text-gray-900 bg-transparent rounded-[4px] border hover:border-black focus:outline-none focus:ring-1 focus:ring-blue border-gray-300 peer"
                value={addressTwo}
                onChange={(e) => setAddressTwo(e.target.value)}
              />
              <label
                htmlFor="delivery_address2_input"
                className="absolute text-md text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-5 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-5 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
              >
                Apt #, suite, floor, or company
              </label>
            </div>
            <div className="relative">
              <PreferenceDropdown
                preference={preference}
                setPreference={setPreference}
              />
            </div>
            <div className="relative">
              <input
                type="text"
                id="delivery_instructions_input"
                className="block px-2.5 h-[58px] py-2.5 w-full text-gray-900 bg-transparent rounded-[4px] border hover:border-black focus:outline-none focus:ring-1 focus:ring-blue border-gray-300 peer"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <label
                htmlFor="delivery_instructions_input"
                className="absolute text-md text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-5 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-5 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
              >
                Delivery instructions
              </label>
            </div>
          </div>
          {setDefaultDeliveryAllowed && (
            <div className="flex row-auto items-center mt-10">
              <input
                id="set-default-delivery-input"
                type="checkbox"
                checked={isDefaultDelivery}
                onChange={(e) => {
                  setIsDefaultDelivery(e.target.checked);
                }}
                className="group block size-8 rounded border bg-white data-[checked]:bg-blue-500 cursor-pointer"
              />
              <label
                htmlFor="set-default-delivery-input"
                className="ml-2 cursor-pointer select-none"
              >
                Set these delivery settings as default
              </label>
            </div>
          )}
          <ConfirmButton
            style={{ backgroundColor: merchantColor }}
            className="px-16 py-4 mt-10 max-w-full text-xl font-medium leading-7 text-center text-white whitespace-nowrap bg-[#228800] disabled:bg-[#C7C7C7] rounded-lg w-[250px] max-md:px-5 max-md:mt-10"
            onClick={() => {
              handleSubmit();
            }}
          >
            <p>Save</p>
          </ConfirmButton>
        </div>
      </div>
    </ReactModal>
  );
};
